/**
 * Tijdlijnwiskunde.
 *
 * Dit bestand is de ENIGE plek waar tijdlijnposities worden omgerekend.
 * De webapp (preview) en de worker (export) gebruiken allebei deze functies,
 * zodat preview en export per definitie hetzelfde rekenen.
 *
 * Alle tijden zijn frames (integers) op de project-fps. Nooit floats voor
 * posities: dat voorkomt drift tussen preview en export.
 */

import type { ProjectModel, TimelineItem, Track, TrackType, VideoItem, AudioItem } from './projectmodel.js';
import { PROJECT_FPS } from './constanten.js';

export function secondeNaarFrame(seconde: number, fps: number = PROJECT_FPS): number {
  return Math.round(seconde * fps);
}

export function frameNaarSeconde(frame: number, fps: number = PROJECT_FPS): number {
  return frame / fps;
}

/** Positie van een item in seconden op de tijdlijn. */
export function itemStartSeconde(item: TimelineItem, fps: number): number {
  return frameNaarSeconde(item.startFrame, fps);
}

export function itemEindFrame(item: TimelineItem): number {
  return item.startFrame + item.duurInFrames;
}

/** Laatste frame dat het item nog zichtbaar is (exclusief einde). */
export function itemLaatsteFrame(item: TimelineItem): number {
  return itemEindFrame(item) - 1;
}

export function isItemActiefOpFrame(item: TimelineItem, frame: number): boolean {
  if (item.verborgen) return false;
  return frame >= item.startFrame && frame < itemEindFrame(item);
}

/**
 * De duur van het project: het laatste frame waarop nog iets gebeurt.
 * Een expliciete override wint, maar nooit minder dan de inhoud.
 */
export function berekenDuurInFrames(project: ProjectModel): number {
  let laatste = 0;
  for (const track of project.tracks) {
    for (const item of track.items) {
      laatste = Math.max(laatste, itemEindFrame(item));
    }
  }
  if (project.duurOverrideInFrames !== null) {
    laatste = Math.max(laatste, project.duurOverrideInFrames);
  }
  return Math.max(laatste, 1);
}

/**
 * Omrekening van tijdlijnframe naar positie in het bronbestand.
 * Dit is de kern van het trimmen: knippunten en preview gebruiken dit.
 */
export function bronFrameVoorItem(
  item: Pick<VideoItem | AudioItem, 'startFrame' | 'bronStartFrame'>,
  tijdlijnFrame: number,
  snelheid = 1,
): number {
  const offset = tijdlijnFrame - item.startFrame;
  // Nooit vóór de trim-offset en nooit negatief: de tijdlijn mag geen frame
  // opvragen dat in de bron niet bestaat. Buiten het item levert dit dus
  // de trim-offset zelf op, wat de veilige waarde is.
  return Math.max(item.bronStartFrame, item.bronStartFrame + Math.round(offset * snelheid));
}

/** Bronpositie in seconden, voor ffmpeg en voor de speler. */
export function bronSecondeVoorItem(
  item: Pick<VideoItem | AudioItem, 'startFrame' | 'bronStartFrame'>,
  tijdlijnFrame: number,
  fps: number,
  snelheid = 1,
): number {
  return bronFrameVoorItem(item, tijdlijnFrame, snelheid) / fps;
}

export interface BronSegment {
  /** Startpunt in het bronbestand, in frames op de bron-fps. */
  bronStartFrame: number;
  /** Lengte in frames op de bron-fps. */
  bronDuurInFrames: number;
  /** Startpunt in seconden; dit gaat rechtstreeks naar ffmpeg (-ss). */
  startSeconde: number;
  /** Lengte in seconden; dit gaat rechtstreeks naar ffmpeg (-t). */
  duurSeconde: number;
}

/**
 * Vertaal een item naar een segment in het bronbestand.
 *
 * `bronFps` is de fps van het bronbestand. Wij rekenen hier bewust in
 * seconden voor ffmpeg, met de frame-offset omgerekend via de PROJECT-fps
 * (want `bronStartFrame` is in projectframes opgeslagen).
 */
export function bronSegmentVoorItem(
  item: Pick<VideoItem | AudioItem, 'startFrame' | 'bronStartFrame' | 'duurInFrames'>,
  projectFps: number,
  snelheid = 1,
): BronSegment {
  const startSeconde = item.bronStartFrame / projectFps;
  const duurSeconde = (item.duurInFrames / projectFps) * snelheid;
  return {
    bronStartFrame: item.bronStartFrame,
    bronDuurInFrames: item.duurInFrames,
    startSeconde,
    duurSeconde,
  };
}

/** Items op een track, gesorteerd op starttijd (en daarna op id voor stabiliteit). */
export function gesorteerdeItems(track: Track): TimelineItem[] {
  return [...track.items].sort((a, b) => a.startFrame - b.startFrame || a.id.localeCompare(b.id));
}

export function vindTrack(project: ProjectModel, trackId: string): Track | undefined {
  return project.tracks.find((t) => t.id === trackId);
}

export function vindItem(
  project: ProjectModel,
  itemId: string,
): { track: Track; item: TimelineItem } | undefined {
  for (const track of project.tracks) {
    const item = track.items.find((i) => i.id === itemId);
    if (item) return { track, item };
  }
  return undefined;
}

export function tracksVanType(project: ProjectModel, type: TrackType): Track[] {
  return project.tracks.filter((t) => t.type === type);
}

/**
 * Vindt het eerstvolgende vrije startframe op een track voor een item van
 * `duurInFrames`, zodat nieuwe clips niet over bestaande heen vallen.
 */
export function volgendVrijFrame(track: Track, duurInFrames: number): number {
  const items = gesorteerdeItems(track);
  let kandidaat = 0;
  for (const item of items) {
    if (kandidaat + duurInFrames <= item.startFrame) {
      return kandidaat;
    }
    kandidaat = Math.max(kandidaat, itemEindFrame(item));
  }
  return kandidaat;
}

/** True als twee items op dezelfde track in tijd overlappen. */
export function overlapt(a: TimelineItem, b: TimelineItem): boolean {
  return a.startFrame < itemEindFrame(b) && b.startFrame < itemEindFrame(a);
}

/**
 * Lijst met knippunten (frames) die als snapdoelen dienen: begin en einde
 * van elk item, plus de projectgrenzen.
 */
export function snapDoelen(project: ProjectModel, negeerItemId?: string): number[] {
  const doelen = new Set<number>([0, berekenDuurInFrames(project)]);
  for (const track of project.tracks) {
    for (const item of track.items) {
      if (item.id === negeerItemId) continue;
      doelen.add(item.startFrame);
      doelen.add(itemEindFrame(item));
    }
  }
  return [...doelen].sort((a, b) => a - b);
}

export interface SnapResultaat {
  frame: number;
  snapped: boolean;
  doel?: number;
}

/**
 * Snapt een frame naar het dichtstbijzijnde doel binnen `drempel` frames.
 */
export function snapFrame(frame: number, doelen: number[], drempel: number): SnapResultaat {
  let beste: number | undefined;
  let besteAfstand = Number.POSITIVE_INFINITY;
  for (const doel of doelen) {
    const afstand = Math.abs(doel - frame);
    if (afstand <= drempel && afstand < besteAfstand) {
      beste = doel;
      besteAfstand = afstand;
    }
  }
  if (beste === undefined) return { frame, snapped: false };
  return { frame: beste, snapped: true, doel: beste };
}

/**
 * Beeldvullende schaal voor een bronbeeld in een kader, met de gekozen
 * passing. Wordt door zowel de preview als de compositie gebruikt.
 */
export interface Afmetingen {
  breedte: number;
  hoogte: number;
}

export interface SchaalResultaat {
  schaal: number;
  /** Hoeveel van het bronbeeld buiten het kader valt (bij bijsnijden). */
  x: number;
  y: number;
}

export function berekenPassing(
  bron: Afmetingen,
  kader: Afmetingen,
  passing: 'bevatten' | 'bijsnijden' | 'uitrekken',
): SchaalResultaat {
  if (bron.breedte <= 0 || bron.hoogte <= 0) {
    return { schaal: 1, x: 0, y: 0 };
  }
  const schaalX = kader.breedte / bron.breedte;
  const schaalY = kader.hoogte / bron.hoogte;
  if (passing === 'uitrekken') {
    return { schaal: 1, x: 0, y: 0 };
  }
  const schaal = passing === 'bijsnijden' ? Math.max(schaalX, schaalY) : Math.min(schaalX, schaalY);
  const geschaald: Afmetingen = { breedte: bron.breedte * schaal, hoogte: bron.hoogte * schaal };
  return {
    schaal,
    x: (kader.breedte - geschaald.breedte) / 2,
    y: (kader.hoogte - geschaald.hoogte) / 2,
  };
}

/** Alle items die op een frame zichtbaar/actief zijn, over alle tracks. */
export function actieveItems(project: ProjectModel, frame: number): Array<{ track: Track; item: TimelineItem }> {
  const resultaat: Array<{ track: Track; item: TimelineItem }> = [];
  for (const track of project.tracks) {
    if (track.verborgen) continue;
    for (const item of track.items) {
      if (isItemActiefOpFrame(item, frame)) {
        resultaat.push({ track, item });
      }
    }
  }
  return resultaat;
}
